import { useState } from 'react';
import axios from 'axios';
import { API } from '../../config/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Heart } from 'lucide-react';

export function DonationsSection({ siteSettings, isVisible }) {
  const [donationAmount, setDonationAmount] = useState('');
  const [donationName, setDonationName] = useState('');
  const [donationEmail, setDonationEmail] = useState('');
  const [donationMessage, setDonationMessage] = useState('');
  const [donating, setDonating] = useState(false);

  if (!isVisible('donations')) return null;

  const handleDonate = async (e) => {
    e.preventDefault();
    const amount = parseFloat(donationAmount);
    if (!amount || amount < 1) return toast.error('Minimum donation is $1.00');
    if (amount > 10000) return toast.error('Maximum donation is $10,000');
    setDonating(true);
    try {
      const { data } = await axios.post(`${API}/donate`, {
        amount,
        origin_url: window.location.origin,
        name: donationName || null,
        email: donationEmail || null,
        message: donationMessage || null,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process donation');
      setDonating(false);
    }
  };

  return (
    <section className="border-t border-[#262626] py-16 sm:py-24 px-4 sm:px-8 bg-[#0A0A0A]" data-testid="donation-section" id="donate">
      <div className="max-w-xl mx-auto text-center">
        <p className="overline text-[var(--accent,#DAA520)] mb-2">{siteSettings?.section_titles?.donations_label || 'SUPPORT THE MOVEMENT'}</p>
        <h2 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-4">
          {siteSettings?.section_titles?.donations || 'DONATE'}
        </h2>
        <p className="text-[#A3A3A3] text-sm mb-8 leading-relaxed">
          Your contribution directly fuels new music, content, and this independent journey. Every dollar makes a difference.
        </p>
        <form onSubmit={handleDonate} className="space-y-4 text-left">
          <div>
            <Label className="overline text-xs">Amount (USD)</Label>
            <div className="relative mt-2">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--accent,#DAA520)] font-mono font-bold text-lg">$</span>
              <Input
                type="number"
                step="0.01"
                min="1"
                max="10000"
                value={donationAmount}
                onChange={(e) => setDonationAmount(e.target.value)}
                className="bg-[#141414] border-[#262626] focus:border-[var(--accent,#DAA520)] rounded-none pl-8 h-14 text-2xl font-mono text-[var(--accent,#DAA520)]"
                placeholder="0.00"
                required
                data-testid="donation-amount-input"
              />
            </div>
            <div className="flex gap-2 mt-3">
              {[5, 10, 25, 50, 100].map(amt => (
                <button key={amt} type="button" onClick={() => setDonationAmount(String(amt))}
                  className={`flex-1 py-2 border text-sm font-bold font-mono transition-colors ${
                    donationAmount === String(amt)
                      ? 'bg-[var(--accent,#DAA520)] border-[var(--accent,#DAA520)] text-black'
                      : 'border-[#262626] text-[#A3A3A3] hover:border-[var(--accent,#DAA520)] hover:text-[var(--accent,#DAA520)]'
                  }`}
                  data-testid={`donation-preset-${amt}`}
                >
                  ${amt}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline text-xs">Name (optional)</Label>
              <Input
                value={donationName}
                onChange={(e) => setDonationName(e.target.value)}
                className="mt-2 bg-[#141414] border-[#262626] focus:border-[var(--accent,#DAA520)] rounded-none"
                placeholder="Your name"
                data-testid="donation-name-input"
              />
            </div>
            <div>
              <Label className="overline text-xs">Email (optional)</Label>
              <Input
                type="email"
                value={donationEmail}
                onChange={(e) => setDonationEmail(e.target.value)}
                className="mt-2 bg-[#141414] border-[#262626] focus:border-[var(--accent,#DAA520)] rounded-none"
                placeholder="your@email.com"
                data-testid="donation-email-input"
              />
            </div>
          </div>
          <div>
            <Label className="overline text-xs">Leave a message (optional)</Label>
            <textarea
              value={donationMessage}
              onChange={(e) => setDonationMessage(e.target.value)}
              rows={3}
              className="mt-2 w-full bg-[#141414] border border-[#262626] focus:border-[var(--accent,#DAA520)] p-3 text-white resize-none outline-none text-sm"
              placeholder="Keep going! Love the music..."
              data-testid="donation-message-input"
            />
          </div>
          <Button type="submit" disabled={donating || !donationAmount}
            className="w-full bg-[var(--accent,#DAA520)] hover:bg-white hover:text-[#0A0A0A] text-black rounded-none h-14 text-base font-bold uppercase tracking-wider"
            data-testid="donate-submit-btn">
            {donating ? (
              <><span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" /> PROCESSING...</>
            ) : (
              <><Heart className="w-5 h-5 mr-2" /> DONATE {donationAmount ? `$${parseFloat(donationAmount).toFixed(2)}` : ''}</>
            )}
          </Button>
          <p className="text-center text-[10px] text-[#666] mt-2">Secure payment via Stripe. Card, PayPal & Cash App accepted.</p>
        </form>
      </div>
    </section>
  );
}
